import { useState, useCallback, useRef, useEffect } from 'react';
import { getCategorySchema } from '../services/adService';
import type { CategorySchema, UseCategorySchemaReturn } from '../types/ad';
import type { AdField, Subcategory, FieldOption } from '../config/postAdTypes';

const schemaCache: Map<string, { schema: CategorySchema; timestamp: number }> = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

const getFieldType = (fieldName: string): AdField['type'] => {
  // Booleans first — `is_price_negotiable` would otherwise match the `price` rule below.
  const booleanNames = new Set([
    // Community/Services
    'membership_required', 'available_for_hire', 'certificate_provided', 'reward', 'online', 'remote_available', 'starting_from',
    // Pets
    'vaccinated', 'dewormed', 'trained', 'tamed', 'pair_available', 'cage_included', 'aquarium_included', 'delivery_available', 'certified', 'emergency_service'
  ]);
  if (fieldName.startsWith('is_') || fieldName.startsWith('has_') || fieldName.startsWith('show_') || booleanNames.has(fieldName)) return 'checkbox';
  if (fieldName.includes('description')) return 'textarea';
  if (
    fieldName.includes('price') || fieldName.includes('rent') || fieldName.includes('deposit') ||
    fieldName.includes('year') || fieldName.includes('mileage') || fieldName.includes('group_size') ||
    fieldName.includes('reward_amount') || fieldName.includes('area_size') || fieldName.includes('total_units') ||
    fieldName.includes('occupancy') || fieldName.includes('age_value') || fieldName === 'quantity'
  ) return 'number';
  if (fieldName.includes('date') || fieldName.includes('valid_until') || fieldName === 'expiry_date' || fieldName === 'available_from' || fieldName === 'possession_date') return 'date';
  if (fieldName.includes('fuel') || fieldName.includes('transmission') || fieldName.includes('condition')) return 'select';
  if (fieldName.includes('image')) return 'file';
  return 'text';
};

// Get placeholder based on field name and selected category schema (PK-focused)
const getPlaceholder = (fieldName: string, schema?: CategorySchema): string => {
  const base: Record<string, string> = {
    title: 'e.g. Toyota Corolla 2020 - Low Mileage',
    description: 'Describe your item in detail...',
    price: 'e.g. 2500000',
    city: 'e.g. Karachi, Lahore, Islamabad',
    location: 'e.g. DHA Phase 6',
    contact_number: '03XX XXXXXXX',
    make: 'e.g. Toyota, Honda, Suzuki',
    model: 'e.g. Corolla XLi',
    year: 'e.g. 2020',
    mileage: 'e.g. 50000',
    fuel_type: 'Select fuel type',
    transmission: 'Select transmission',
  };

  const slug = (schema?.category_slug || '').toLowerCase();

  // Motorbikes & Scooters
  if (slug === 'motorbikes-scooters') {
    const bikes: Record<string, string> = {
      title: 'e.g. Honda CG125 2022 – Low Mileage',
      make: 'e.g. Honda, Yamaha, Suzuki',
      model: 'e.g. CG125, YBR125, GS150',
      year: 'e.g. 2022',
      mileage: 'e.g. 15000',
      fuel_type: 'Petrol or Electric',
      transmission: 'Manual or CVT',
      city: 'e.g. Lahore',
      location: 'e.g. Johar Town',
    };
    return bikes[fieldName] || base[fieldName] || '';
  }

  // For Sale — Mobile Phones
  if (slug === 'mobile-phones') {
    const phones: Record<string, string> = {
      title: 'e.g. iPhone 13 Pro 128GB PTA Approved',
      make: 'e.g. Apple, Samsung, Xiaomi',
      model: 'e.g. iPhone 13 Pro, Galaxy S21',
      storage: 'e.g. 128GB',
      ram: 'e.g. 6GB',
      pta_approved: 'Select PTA approval status',
      city: 'e.g. Lahore',
      location: 'e.g. Model Town',
    };
    return phones[fieldName] || base[fieldName] || '';
  }

  // For Sale — Electronics
  if (slug === 'electronics') {
    const el: Record<string, string> = {
      title: 'e.g. Dell XPS 13 i7 16GB 512GB',
      make: 'e.g. Dell, HP, Samsung, LG',
      model: 'e.g. XPS 13, Series 7',
      city: 'e.g. Karachi',
      location: 'e.g. Gulshan-e-Iqbal',
    };
    return el[fieldName] || base[fieldName] || '';
  }

  // For Sale — Fashion
  if (slug === 'fashion') {
    const fa: Record<string, string> = {
      title: 'e.g. Khaadi Lawn 3-Piece – Unstitched',
      make: 'e.g. Khaadi, Sapphire, Gul Ahmed',
      model: 'e.g. Summer Collection',
      city: 'e.g. Islamabad',
      location: 'e.g. F-7',
    };
    return fa[fieldName] || base[fieldName] || '';
  }

  // For Sale — Furniture
  if (slug === 'furniture') {
    const fu: Record<string, string> = {
      title: 'e.g. 6-Seater Wooden Dining Table',
      make: 'e.g. Interwood, Habitt',
      model: 'e.g. Roman/Modern Style',
      city: 'e.g. Faisalabad',
      location: 'e.g. Peoples Colony',
    };
    return fu[fieldName] || base[fieldName] || '';
  }

  // For Sale — Home & Garden
  if (slug === 'home-garden') {
    const hg: Record<string, string> = {
      title: 'e.g. Philips Blender HR-2056',
      make: 'e.g. Philips, Anex, WestPoint',
      model: 'e.g. HR2056/90',
      city: 'e.g. Rawalpindi',
      location: 'e.g. Satellite Town',
    };
    return hg[fieldName] || base[fieldName] || '';
  }

  // For Sale — Sports & Leisure
  if (slug === 'sports-leisure') {
    const sp: Record<string, string> = {
      title: 'e.g. Trek Marlin 5 Mountain Bike',
      make: 'e.g. Trek, Adidas, Nike',
      model: 'e.g. Marlin 5',
      city: 'e.g. Multan',
      location: 'e.g. Gulgasht',
    };
    return sp[fieldName] || base[fieldName] || '';
  }

  // Services — Home Services
  if (slug === 'home-services') {
    const hs: Record<string, string> = {
      title: 'e.g. Electrician for Same-Day Fix',
      service_type: 'e.g. electrician, plumber, ac_repair',
      pricing_type: 'Select pricing model',
      city: 'e.g. Karachi',
      location: 'e.g. Gulshan-e-Iqbal',
    };
    return hs[fieldName] || base[fieldName] || '';
  }

  // Services — Beauty & Health
  if (slug === 'beauty-health') {
    const bh: Record<string, string> = {
      title: 'e.g. Bridal Makeup at Home/Salon',
      service_type: 'e.g. salon, makeup, skincare',
      pricing_type: 'Select pricing model',
      city: 'e.g. Lahore',
      location: 'e.g. DHA Phase 6',
    };
    return bh[fieldName] || base[fieldName] || '';
  }

  // Services — Event Services
  if (slug === 'event-services') {
    const ev: Record<string, string> = {
      title: 'e.g. Wedding Photography & Videography',
      service_type: 'e.g. photography, catering, decoration',
      pricing_type: 'Select pricing model',
      city: 'e.g. Islamabad',
      location: 'e.g. Blue Area',
    };
    return ev[fieldName] || base[fieldName] || '';
  }

  // Services — IT & Technical
  if (slug === 'it-technical') {
    const it: Record<string, string> = {
      title: 'e.g. WordPress Website Development',
      service_type: 'e.g. web_development, seo, mobile_app',
      pricing_type: 'Select pricing model',
      city: 'e.g. Faisalabad',
      location: 'e.g. People’s Colony',
    };
    return it[fieldName] || base[fieldName] || '';
  }

  // Services — Education & Training
  if (slug === 'education-training') {
    const ed: Record<string, string> = {
      title: 'e.g. Math Home Tuition (Intermediate)',
      service_type: 'e.g. home_tuition, online_tuition, academy',
      pricing_type: 'Select pricing model',
      city: 'e.g. Rawalpindi',
      location: 'e.g. Satellite Town',
    };
    return ed[fieldName] || base[fieldName] || '';
  }

  // Services — Other Services
  if (slug === 'other-services') {
    const os: Record<string, string> = {
      title: 'e.g. Visa Consulting & Travel Planning',
      service_type: 'e.g. consulting, legal, travel, visa',
      pricing_type: 'Select pricing model',
      city: 'e.g. Multan',
      location: 'e.g. Gulgasht',
    };
    return os[fieldName] || base[fieldName] || '';
  }

  // Pets — Dogs
  if (slug === 'dogs') {
    const dg: Record<string, string> = {
      title: 'e.g. German Shepherd – 8 Months, Vaccinated',
      breed: 'e.g. German Shepherd, Labrador, Local',
      age_value: 'e.g. 8',
      age_unit: 'months or years',
      gender: 'male or female',
      size: 'small / medium / large',
      color: 'e.g. black & tan',
    };
    return dg[fieldName] || base[fieldName] || '';
  }

  // Pets — Cats
  if (slug === 'cats') {
    const ct: Record<string, string> = {
      title: 'e.g. Persian Cat – 1.5 Years, Litter Trained',
      breed: 'e.g. Persian, Siamese, Local',
      age_value: 'e.g. 18',
      age_unit: 'months or years',
      gender: 'male or female',
      color: 'e.g. white',
    };
    return ct[fieldName] || base[fieldName] || '';
  }

  // Pets — Birds
  if (slug === 'birds') {
    const bd: Record<string, string> = {
      title: 'e.g. Pair of Lovebirds – Tamed',
      species: 'e.g. parrot, pigeon, lovebirds',
      breed: 'Optional breed (if any)',
      age_value: 'e.g. 6',
      age_unit: 'months or years',
      gender: 'male / female / unknown',
    };
    return bd[fieldName] || base[fieldName] || '';
  }

  // Pets — Fish & Aquarium
  if (slug === 'fish-aquarium') {
    const fa: Record<string, string> = {
      title: 'e.g. Freshwater Goldfish – 10 Qty',
      fish_type: 'freshwater or saltwater',
      species: 'e.g. goldfish, guppy, koi',
      quantity: 'e.g. 10',
      tank_size: 'e.g. 3ft x 1.5ft x 1.5ft',
    };
    return fa[fieldName] || base[fieldName] || '';
  }

  // Pets — Pet Food & Accessories
  if (slug === 'pet-food-accessories') {
    const pfa: Record<string, string> = {
      title: 'e.g. Royal Canin Dog Food 10kg',
      product_type: 'food, cage, leash, toys, grooming, medicine, other',
      pet_type: 'dog, cat, bird, fish, all',
      brand: 'e.g. Royal Canin, Pedigree',
      expiry_date: 'Expiry date (if food/medicine)',
    };
    return pfa[fieldName] || base[fieldName] || '';
  }

  // Pets — Pet Services
  if (slug === 'pet-services') {
    const ps: Record<string, string> = {
      title: 'e.g. Pet Grooming at Home',
      service_type: 'grooming, training, vet, boarding, walking, breeding, other',
      service_mode: 'home_service, shop, clinic',
      experience_years: 'e.g. 3',
      service_area: 'e.g. Lahore – DHA, Gulberg',
    };
    return ps[fieldName] || base[fieldName] || '';
  }

  // Community — Events
  if (slug === 'events') {
    const ev: Record<string, string> = {
      title: 'e.g. Tech Meetup – Saturday',
      event_date: 'Pick the event date',
      organization: 'e.g. Local Dev Community',
      external_link: 'e.g. https://example.com/register',
      city: 'e.g. Karachi',
      location: 'e.g. Zamzama',
    };
    return ev[fieldName] || base[fieldName] || '';
  }

  // Community — Activities & Hobbies
  if (slug === 'activities-hobbies') {
    const ah: Record<string, string> = {
      title: 'e.g. Weekend Football Group',
      activity_type: 'e.g. sports, gaming, book_club',
      skill_level: 'Beginner/Intermediate/Advanced',
      group_size: 'e.g. 12',
      schedule: 'e.g. Sat-Sun 7–9am',
      membership_required: 'Select if membership is required',
      city: 'e.g. Lahore',
      location: 'e.g. Gulberg',
    };
    return ah[fieldName] || base[fieldName] || '';
  }

  // Community — Artists & Musicians
  if (slug === 'artists-musicians') {
    const am: Record<string, string> = {
      title: 'e.g. Acoustic Band Available',
      artist_type: 'e.g. singer, band, dj',
      genre: 'e.g. pop, rock, qawwali',
      available_for_hire: 'Tick if available for gigs',
      performance_type: 'Live, studio or both',
      portfolio_links: 'Add your social links',
      city: 'e.g. Islamabad',
      location: 'e.g. F-10',
    };
    return am[fieldName] || base[fieldName] || '';
  }

  // Community — Lost & Found
  if (slug === 'lost-found') {
    const lf: Record<string, string> = {
      title: 'e.g. Lost – Brown Wallet',
      lost_found_type: 'Select lost or found',
      item_type: 'e.g. documents, mobile, pet',
      lost_date: 'Pick the date',
      location_details: 'Nearest place or landmark',
      reward_amount: 'e.g. 5000 (optional)',
      city: 'e.g. Rawalpindi',
      location: 'e.g. Commercial Market',
    };
    return lf[fieldName] || base[fieldName] || '';
  }

  // Community — Volunteering
  if (slug === 'volunteering') {
    const vo: Record<string, string> = {
      title: 'e.g. Teach Kids – Evening Classes',
      cause: 'e.g. education, healthcare',
      organization: 'e.g. ABC Foundation',
      volunteer_type: 'part_time, full_time, remote',
      duration: 'e.g. 3 months',
      city: 'e.g. Multan',
      location: 'e.g. Cantt',
    };
    return vo[fieldName] || base[fieldName] || '';
  }

  // Community — Announcements
  if (slug === 'announcements') {
    const an: Record<string, string> = {
      title: 'e.g. Job Fair – DHA Sports Club',
      announcement_type: 'e.g. public_notice, job_fair',
      valid_until: 'Pick last date for visibility',
      organization: 'e.g. City Govt / Company',
      external_link: 'e.g. https://example.com/details',
      priority: 'normal or high',
      city: 'e.g. Faisalabad',
      location: 'e.g. Jaranwala Road',
    };
    return an[fieldName] || base[fieldName] || '';
  }

  // Property — For Sale
  if (slug === 'for-sale') {
    const ps: Record<string, string> = {
      title: 'e.g. 10 Marla House in DHA Phase 6',
      purpose: 'Select sale or rent',
      property_type: 'e.g. house, flat, plot',
      area_size: 'e.g. 10',
      area_unit: 'marla, kanal or sqft',
      address: 'Society / Phase / Block / Street',
      city: 'e.g. Lahore',
      location: 'e.g. DHA Phase 6',
    };
    return ps[fieldName] || base[fieldName] || '';
  }

  // Property — For Rent
  if (slug === 'for-rent') {
    const pr: Record<string, string> = {
      title: 'e.g. 2 Bed Flat – Monthly Rent',
      purpose: 'Select rent',
      property_type: 'e.g. house, flat, portion',
      monthly_rent: 'e.g. 85000',
      security_deposit: 'e.g. 170000',
      available_from: 'Pick availability date',
      city: 'e.g. Karachi',
      location: 'e.g. Gulshan-e-Iqbal',
    };
    return pr[fieldName] || base[fieldName] || '';
  }

  // Property — New Projects
  if (slug === 'new-projects') {
    const np: Record<string, string> = {
      title: 'e.g. Bahria Orchard – New Blocks',
      project_name: 'e.g. Bahria Orchard Phase 2',
      developer: 'e.g. Bahria Town',
      project_type: 'e.g. housing_scheme',
      location_landmark: 'Nearest landmark',
      total_units: 'e.g. 200',
      possession_date: 'Expected possession date',
      city: 'e.g. Islamabad',
      location: 'e.g. Expressway',
    };
    return np[fieldName] || base[fieldName] || '';
  }

  // Property — Commercial
  if (slug === 'commercial') {
    const cm: Record<string, string> = {
      title: 'e.g. Shop for Sale – Main Market',
      property_type: 'e.g. shop, office, warehouse',
      area_size: 'e.g. 600',
      area_unit: 'sqft',
      city: 'e.g. Faisalabad',
      location: 'e.g. D-Ground',
    };
    return cm[fieldName] || base[fieldName] || '';
  }

  // Property — Rooms & Flatmates
  if (slug === 'rooms-flatmates') {
    const rf: Record<string, string> = {
      title: 'e.g. Shared Room for Female – DHA',
      room_type: 'single or shared',
      rent: 'e.g. 25000',
      available_from: 'Pick date',
      house_rules: 'e.g. no_smoking, no_pets',
      city: 'e.g. Lahore',
      location: 'e.g. DHA Phase 3',
    };
    return rf[fieldName] || base[fieldName] || '';
  }

  // Vans & Buses
  if (slug === 'vans-buses') {
    const vans: Record<string, string> = {
      title: 'e.g. Toyota Hiace 2016 – 12 Seater',
      make: 'e.g. Toyota, Suzuki, Hino',
      model: 'e.g. Hiace, Bolan, Coaster',
      year: 'e.g. 2016',
      mileage: 'e.g. 120000',
      fuel_type: 'Diesel, Petrol, Hybrid',
      transmission: 'Manual or Automatic',
      city: 'e.g. Karachi',
      location: 'e.g. Saddar',
    };
    return vans[fieldName] || base[fieldName] || '';
  }

  // Trucks & Heavy Vehicles
  if (slug === 'trucks-heavy-vehicles') {
    const trucks: Record<string, string> = {
      title: 'e.g. Hino FM 2008 – 22 Ton',
      make: 'e.g. Hino, Isuzu, FAW, Bedford',
      model: 'e.g. FM, NPR, J5',
      year: 'e.g. 2008',
      mileage: 'e.g. 350000',
      fuel_type: 'Diesel',
      transmission: 'Manual',
      city: 'e.g. Faisalabad',
      location: 'e.g. Jhang Road',
    };
    return trucks[fieldName] || base[fieldName] || '';
  }

  // Auto Parts & Accessories
  if (slug === 'auto-parts-accessories') {
    const parts: Record<string, string> = {
      title: 'e.g. Toyota Corolla 2015 Headlight (Genuine)',
      make: 'e.g. Toyota, Honda, Suzuki',
      model: 'e.g. Corolla 2015',
      city: 'e.g. Rawalpindi',
      location: 'e.g. Raja Bazaar',
    };
    return parts[fieldName] || base[fieldName] || '';
  }

  // Other Vehicles
  if (slug === 'other-vehicles') {
    const other: Record<string, string> = {
      title: 'e.g. New Asia Loader Rickshaw 2021',
      make: 'e.g. New Asia, Qingqi',
      model: 'e.g. Loader, 200cc',
      year: 'e.g. 2021',
      mileage: 'e.g. 20000',
      fuel_type: 'Petrol, Diesel, Electric',
      city: 'e.g. Multan',
      location: 'e.g. Chowk Kumharan',
    };
    return other[fieldName] || base[fieldName] || '';
  }

  // Default fallbacks
  return base[fieldName] || '';
};

/**
 * Build AdField array from schema field names and options
 */
const buildFieldsFromSchema = (
  fieldNames: string[],
  schema: CategorySchema,
  isRequired: boolean
): AdField[] => {
  if (!fieldNames || fieldNames.length === 0) return [];
  return fieldNames.map(fieldName => {
    let type = getFieldType(fieldName);

    // Build options for select fields
    let options: FieldOption[] | undefined;
    // Prefer backend-provided select_options when available
    const backendOptions = (schema as any).select_options?.[fieldName] as string[] | undefined;
    if (backendOptions && Array.isArray(backendOptions) && backendOptions.length > 0) {
      // Render equipment_included as a multi-select checkbox group
      if (fieldName === 'equipment_included') {
        type = 'checkbox_group';
      } else {
        type = 'select';
      }
      options = backendOptions.map(v => ({ label: v, value: v }));
    }
    if (fieldName === 'fuel_type' || fieldName === 'fuel') {
      options = schema.fuel_types?.map(f => ({ label: f.charAt(0).toUpperCase() + f.slice(1), value: f }));
    } else if (fieldName === 'transmission') {
      options = schema.transmissions?.map(t => ({ label: t.charAt(0).toUpperCase() + t.slice(1).replace('_', '-'), value: t }));
    } else if (fieldName === 'condition') {
      options = schema.conditions?.map(c => ({ label: c.charAt(0).toUpperCase() + c.slice(1).replace('_', '/'), value: c }));
    }

    // Build validation rules
    let validation: AdField['validation'];
    if (fieldName === 'year') {
      validation = { min: 1950, max: 2026, message: 'Year must be between 1950 and 2026' };
    } else if (fieldName === 'price') {
      validation = { min: 0, message: 'Price must be positive' };
    } else if (fieldName === 'mileage') {
      validation = { min: 0, message: 'Mileage must be positive' };
    }

    return {
      name: fieldName,
      label: fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ').replace(/is /i, ''),
      type,
      required: isRequired,
      placeholder: getPlaceholder(fieldName, schema),
      options,
      validation,
      helpText: schema.field_help_text?.[fieldName],
    };
  });
};

/**
 * Hook for fetching and managing category form schemas
 */
export function useCategorySchema(): UseCategorySchemaReturn {
  const [schema, setSchema] = useState<CategorySchema | null>(null);
  const [fields, setFields] = useState<AdField[]>([]);
  const [commonFields, setCommonFields] = useState<AdField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Fetch schema for a category
   */
  const fetchSchema = useCallback(async (categoryId: string) => {
    if (!categoryId || !isMounted.current) return;

    // Check cache first
    const cached = schemaCache.get(categoryId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (!isMounted.current) return;
      
      const schemaData = cached.schema;
      setSchema(schemaData);
      // Build fields from required + optional field names
      const requiredFields = buildFieldsFromSchema(schemaData.required_fields || [], schemaData, true);
      const optionalFields = buildFieldsFromSchema(schemaData.optional_fields || [], schemaData, false);
      setFields([...requiredFields, ...optionalFields]);
      // Common fields are included in the main fields for this format
      setCommonFields([]);
      setError(null);
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getCategorySchema(categoryId);

      if (!isMounted.current) return;

      const schemaData = response.data;
      
      // Update cache
      schemaCache.set(categoryId, {
        schema: schemaData,
        timestamp: Date.now(),
      });

      setSchema(schemaData);
      // Build fields from required + optional field names
      const requiredFields = buildFieldsFromSchema(schemaData.required_fields || [], schemaData, true);
      const optionalFields = buildFieldsFromSchema(schemaData.optional_fields || [], schemaData, false);
      const allFields = [...requiredFields, ...optionalFields];
      setFields(allFields);
      // Common fields are included in the main fields for this format
      setCommonFields([]);
    } catch (err) {
      if (!isMounted.current) return;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch category schema';
      setError(errorMessage);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const getSubcategoryFields = useCallback((_subcategoryId: string): AdField[] => {
    // New format doesn't have subcategory-specific fields, all in one schema
    return [];
  }, [schema]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setSchema(null);
    setFields([]);
    setCommonFields([]);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    schema,
    fields,
    commonFields,
    isLoading,
    error,
    fetchSchema,
    getSubcategoryFields,
    reset,
  };
}

/**
 * Get cached schema without fetching
 */
export const getCachedSchema = (categoryId: string): CategorySchema | null => {
  const cached = schemaCache.get(categoryId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.schema;
  }
  return null;
};

/**
 * Preload schema for a category
 */
export const preloadCategorySchema = async (categoryId: string): Promise<void> => {
  if (schemaCache.has(categoryId)) return;
  
  try {
    const response = await getCategorySchema(categoryId);
    schemaCache.set(categoryId, {
      schema: response.data,
      timestamp: Date.now(),
    });
  } catch (error) {
    // Silently fail on preload
    console.warn('Failed to preload schema:', error);
  }
};

/**
 * Build subcategories from schema (legacy format only)
 */
export const buildSubcategoriesFromSchema = (_schema: CategorySchema | null): Subcategory[] => {
  // New schema format doesn't have subcategories array
  return [];
};

export default useCategorySchema;
